import React, { memo, useCallback, useEffect, useRef } from 'react';
import { findNodeHandle, StyleSheet, TouchableHighlight } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { TWCBackHandler } from '@components';
import PropTypes from 'prop-types';

import { AlertModalTypes, menuTestIDs, Theme } from '@constants';
import {
  useAllContextReferences,
  useAppContextNodeHandle,
  useAppContextReference,
  useContextReference,
  useNodeHandle,
} from '@hooks';
import { PageProvider } from '@store';

import { isAndroid, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';
import { TWCBackground } from '../TWCBackground/TWCBackground';

const PageContent = memo(
  ({
    hasBackground,
    isMenuItem,
    pageID,
    childIds = [],
    firstFocusID,
    onPageFocus,
    onPageBlur,
    pageStyle,
    children,
  }) => {
    const thisReference = useAppContextReference(pageID);
    const thisNodeHandle = useNodeHandle(thisReference);
    const firstFocusReference = useContextReference(firstFocusID);
    const childReferences = useAllContextReferences();
    const lastFocusedNode = useRef(null);
    const menuReference = useAppContextReference(menuTestIDs.mainMenu);
    const menuHandle = useAppContextNodeHandle(menuTestIDs.mainMenu);

    const lastRoute = useAppContextReference('lastRoute');

    const pageIsVisible = useRef(null);

    const useTVEventHandler = require('react-native').useTVEventHandler || (() => null);

    const myTVEventHandler = event => {
      if (event.eventType === 'focus') {
        const target = event.target;
        const childNodes = childIds.map(id => {
          const childReference = childReferences[id]?.current;

          return childReference ? findNodeHandle(childReference) : null;
        });
        const childIndex = childNodes.indexOf(target);

        if (childIndex >= 0 && childIndex < childIds.length) {
          const reference = childReferences[childIds[childIndex]];

          if (reference) {
            console.log(`${pageID} setting lastFocused to ${childIds[childIndex]}`);
            lastFocusedNode.current = reference;
          }
        } else if (target !== findNodeHandle(thisReference?.current)) {
          const shouldPrevent = onPageBlur?.(target);

          if (shouldPrevent) {
            setTimeout(() => {
              FOCUS_NOW(thisReference);
            });
          }
        }
      }
    };

    useTVEventHandler(myTVEventHandler);

    const onBackPress = () => {
      if (isMenuItem && pageIsVisible.current) {
        FOCUS_NOW(menuReference);
        return true;
      }
    };

    const updateFocusForPage = target => {
      if (isMenuItem && lastRoute.current === AlertModalTypes.exit) {
        return;
      }

      if (!pageIsVisible.current) {
        return;
      }

      const tag = target;

      if (tag === pageID || tag === thisNodeHandle) {
        const updateFocus = onPageFocus ? onPageFocus() : true;

        if (updateFocus) {
          if (lastFocusedNode.current) {
            FOCUS_NOW(lastFocusedNode.current);
          } else if (firstFocusReference && firstFocusReference.current) {
            FOCUS_NOW(firstFocusReference.current);
          } else {
            console.warn(`No focus target for page: ${pageID}`);
          }
        }
      } else {
        if (isWeb) {
          const nodeID = tag;
          const reference = childReferences[nodeID];

          if (reference) {
            console.log(`${pageID} setting lastFocused to ${nodeID}`);
            lastFocusedNode.current = reference;
          } else {
            console.log(`${nodeID} not child of ${pageID}`);
            const shouldPrevent = onPageBlur?.(nodeID);

            if (shouldPrevent) {
              FOCUS_NOW(thisReference);
            }
          }
        }
      }
    };

    const navigation = useNavigation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
      useCallback(() => {
        TWCBackHandler.addEventListener('hardwareBackPress', onBackPress, 1);

        const unsubscribe = navigation.addListener('blur', event => {
          pageIsVisible.current = false;
        });

        return () => {
          unsubscribe();
          TWCBackHandler.removeEventListener('hardwareBackPress', onBackPress);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []),
    );

    useFocusEffect(
      useCallback(() => {
        if (!pageIsVisible.current) {
          pageIsVisible.current = true;

          if (isMenuItem && lastRoute.current === AlertModalTypes.exit) {
            return;
          }

          if (isAndroid && lastRoute.current === 'MainMenu' && isMenuItem) {
            // If being navigated to from a tab selection in MainMenu on Android,
            // don't set the focus.  This prevents the focus ring from flickering
            // due to the focus already being set in onFocus.
            return;
          }

          if (isAndroid) {
            setTimeout(() => {
              FOCUS_NOW(thisReference.current);
            }, 10);
          } else {
            FOCUS_NOW(thisReference.current);
          }
        }
      }, [pageIsVisible, isMenuItem, lastRoute, thisReference]),
    );

    return (
      <TouchableHighlight
        accessibilityHint={''}
        accessibilityLabel={' '}
        activeOpacity={1}
        nativeID={pageID}
        nextFocusDown={null}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={isMenuItem ? menuHandle : null}
        ref={thisReference}
        style={[styles.pageStyle, pageStyle]}
        testID={pageID}
        underlayColor={'clear'}
        onBlur={event => {
          if (isWeb) {
            setTimeout(() => {
              const newID = document.activeElement.id;

              if (!childIds.includes(newID)) {
                const shouldPrevent = onPageBlur?.(newID);

                if (shouldPrevent) {
                  FOCUS_NOW(thisReference);
                }
              }
            });
          }
        }}
        onFocus={focusEvent => {
          const tag = focusEvent.tag;

          updateFocusForPage(tag);
        }}
      >
        <>{hasBackground ? <TWCBackground>{children}</TWCBackground> : <>{children}</>}</>
      </TouchableHighlight>
    );
  },
);

/**
 * A wrapper for each page in the app to handle focus management and interactions with the main menu.
 * All base pages should use TWCPage as their top-most component.
 *
 * TWCPage must be supplied a pageID, which you should define in the testIDs for your page. This should
 * also be the same ID that is used for the route's name in screens and to create a reference to this
 * page in AppProvider.
 *
 * You must also provide a list of childIds. If your page has no focusable children, you can supply an
 * empty array. In this case, the page itself will gain focus initially. If you have one or more child
 * ids in the childIds array, you must specify the firstFocus as the child ID that should gain initial
 * focus. The default behavior for when this page is focused will be to try to focus the last focused
 * element, if available. Otherwise, the page will focus the element specified by firstFocus.
 * Note that context references for the page are only created for each id passed in childIds. If you use
 * useContextReference(id) on this page, you MUST passs that id in childIds.
 *
 * If you do not want to render the basic TWCBackground component, you can specify hasBackground=false.
 * The default value is true.
 *
 * If you want to specify custom style props for the wrapper component, you can pass pageStyle a stylesheet
 * entry.
 *
 * Finally, you can handle custom focus implementations as well by passing a function to onPageFocus. If
 * that function returns true, it is assumed that you handled the focus behavior and the wrapper implementation
 * will not be called. Otherwise, the default behavior will be called.
 *
 * @returns A page wrapper implementation.
 */
export const TWCPage = ({
  hasBackground = true,
  isMenuItem = false,
  pageID,
  childIds,
  firstFocus,
  children,
  pageStyle,
  onPageFocus,
  onPageBlur,
}) => (
  <PageProvider ids={childIds}>
    <PageContent
      childIds={childIds}
      firstFocusID={firstFocus}
      hasBackground={hasBackground}
      isMenuItem={isMenuItem}
      pageID={pageID}
      pageStyle={pageStyle}
      onPageBlur={onPageBlur}
      onPageFocus={onPageFocus}
    >
      {children}
    </PageContent>
  </PageProvider>
);

TWCPage.propTypes = {
  hasBackground: PropTypes.bool,
  isMenuItem: PropTypes.bool,
  pageID: PropTypes.string.isRequired,
  childIds: PropTypes.array.isRequired,
  firstFocus: PropTypes.string,
  pageStyle: PropTypes.object,
  onPageFocus: PropTypes.func,
  onPageBlur: PropTypes.func,
};

const styles = StyleSheet.create({
  pageStyle: {
    height: Theme.px(1080),
    width: Theme.px(1920),
  },
});
